import { useEffect, useState } from "react";

import { fetchAddressMap } from "../services/geolocation";
import type { AddressMapPoint } from "../types";

export type AddressMapState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; address: AddressMapPoint }
  | { status: "error"; error: Error };

type Settled = { addressId: number; state: AddressMapState };

export function useAddressMap(
  addressId: number,
  enabled = true,
): AddressMapState {
  const [settled, setSettled] = useState<Settled | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const controller = new AbortController();

    fetchAddressMap(addressId, { signal: controller.signal })
      .then((address) =>
        setSettled({ addressId, state: { status: "ready", address } }),
      )
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setSettled({
            addressId,
            state: {
              status: "error",
              error: error instanceof Error ? error : new Error(String(error)),
            },
          });
        }
      });

    return () => controller.abort();
  }, [addressId, enabled]);

  if (!enabled) {
    return { status: "idle" };
  }

  return settled?.addressId === addressId
    ? settled.state
    : { status: "loading" };
}
